import { useEffect, useMemo } from "react";

const STYLE_ATTR = "data-ai-tool-ui-style-id";
const mountedStyleRefCounts = new Map();

const getExistingStyleElement = (styleId) => {
  if (typeof document === "undefined") return null;
  const styles = document.head.querySelectorAll(`style[${STYLE_ATTR}]`);
  for (const styleEl of styles) {
    if (styleEl.getAttribute(STYLE_ATTR) === styleId) {
      return styleEl;
    }
  }
  return null;
};

const mountStyleBundle = (bundle) => {
  const styleId = bundle.id;
  const cssText = bundle.cssText;
  const currentCount = mountedStyleRefCounts.get(styleId) || 0;

  if (currentCount === 0) {
    let styleEl = getExistingStyleElement(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.setAttribute(STYLE_ATTR, styleId);
      styleEl.type = "text/css";
      document.head.appendChild(styleEl);
    }
    if (styleEl.textContent !== cssText) {
      styleEl.textContent = cssText;
    }
  }

  mountedStyleRefCounts.set(styleId, currentCount + 1);
};

const unmountStyleBundle = (bundle) => {
  const styleId = bundle.id;
  const currentCount = mountedStyleRefCounts.get(styleId) || 0;

  if (currentCount <= 1) {
    mountedStyleRefCounts.delete(styleId);
    const styleEl = getExistingStyleElement(styleId);
    styleEl?.remove();
    return;
  }

  mountedStyleRefCounts.set(styleId, currentCount - 1);
};

export function AIToolUiStyleHost({ aiToolSystem = null }) {
  const styleBundles = useMemo(() => {
    const rawBundles = aiToolSystem?.getToolUiStyleBundles?.();
    return Array.isArray(rawBundles)
      ? rawBundles.filter(
          (bundle) =>
            bundle &&
            typeof bundle.id === "string" &&
            bundle.id.trim().length > 0 &&
            typeof bundle.cssText === "string" &&
            bundle.cssText.trim().length > 0
        )
      : [];
  }, [aiToolSystem]);

  const styleSignature = useMemo(
    () =>
      styleBundles
        .map((bundle) => `${bundle.id}:${bundle.cssText}`)
        .join("|"),
    [styleBundles]
  );

  useEffect(() => {
    if (typeof document === "undefined" || styleBundles.length === 0) {
      return undefined;
    }

    styleBundles.forEach(mountStyleBundle);
    return () => {
      styleBundles.forEach(unmountStyleBundle);
    };
  }, [styleBundles, styleSignature]);

  return null;
}

export default AIToolUiStyleHost;
